import { useEffect, useMemo, useState } from "react";
import { Loader2, Scissors, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { pdfjsLib, getPdfDocument } from "@/lib/pdfjs";
import { splitPdfByGroups } from "@/lib/pdfSplit";
import { useToast } from "@/hooks/use-toast";

interface PdfSplitterDialogProps {
  file: File | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (parts: File[]) => void;
}

const PdfSplitterDialog = ({ file, open, onClose, onConfirm }: PdfSplitterDialogProps) => {
  const [pages, setPages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  /** Índices de página (1-based) después de los que hay un corte. */
  const [cuts, setCuts] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!open || !file) {
      setPages([]);
      setCuts(new Set());
      return;
    }
    let cancelled = false;
    const isPdf = /\.pdf$/i.test(file.name) || file.type === "application/pdf";
    if (!isPdf) {
      toast({
        title: "Sólo se pueden separar PDFs",
        description: "Este archivo no es un PDF multipágina.",
        variant: "destructive",
      });
      onClose();
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const buf = await file.arrayBuffer();
        if (cancelled) return;
        const pdf = await (await getPdfDocument({ data: buf })).promise;
        const imgs: string[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const vp = page.getViewport({ scale: 0.7 });
          const canvas = document.createElement("canvas");
          canvas.width = vp.width;
          canvas.height = vp.height;
          await page.render({ canvas, canvasContext: canvas.getContext("2d")!, viewport: vp }).promise;
          imgs.push(canvas.toDataURL("image/jpeg", 0.7));
          canvas.width = 0;
          canvas.height = 0;
        }
        if (!cancelled) setPages(imgs);
      } catch (e) {
        console.error("Error rendering PDF for split:", e);
        toast({
          title: "No se pudo leer el PDF",
          description: "Intenta con otro archivo.",
          variant: "destructive",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, file, onClose, toast]);

  const groups = useMemo(() => {
    if (pages.length === 0) return [];
    const g: number[][] = [];
    let current: number[] = [];
    for (let i = 1; i <= pages.length; i++) {
      current.push(i);
      if (i < pages.length && cuts.has(i)) {
        g.push(current);
        current = [];
      }
    }
    if (current.length) g.push(current);
    return g;
  }, [cuts, pages.length]);

  const toggleCut = (afterPage: number) => {
    setCuts((prev) => {
      const next = new Set(prev);
      if (next.has(afterPage)) next.delete(afterPage);
      else next.add(afterPage);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!file || groups.length < 2) return;
    setSaving(true);
    try {
      const parts = await splitPdfByGroups(file, groups);
      onConfirm(parts);
    } catch (e) {
      console.error("Split failed:", e);
      toast({
        title: "Error al separar",
        description: e instanceof Error ? e.message : "Fallo desconocido.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl w-[90vw] h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Scissors className="h-4 w-4" /> Separar documento
          </DialogTitle>
          <DialogDescription className="text-xs">
            Pulsa entre dos páginas para marcar el corte. Cada grupo se convertirá en un nuevo archivo
            pendiente y se renumerará el nº de IVA al procesarlos.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto border rounded-md p-4 bg-muted/20">
          {loading ? (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Cargando páginas…
            </div>
          ) : (
            <div className="space-y-3">
              {groups.map((grp, gi) => (
                <div key={gi} className="border-2 border-dashed border-primary/40 rounded-md p-3 bg-background">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                    Documento {gi + 1} · {grp.length} pág.
                  </div>
                  <div className="flex flex-wrap items-stretch gap-2">
                    {grp.map((pageNum, idx) => (
                      <div key={pageNum} className="flex items-center gap-2">
                        <div className="flex flex-col items-center">
                          <img
                            src={pages[pageNum - 1]}
                            alt={`Página ${pageNum}`}
                            className="border rounded max-h-48 shadow-sm"
                          />
                          <span className="text-[10px] text-muted-foreground mt-1">Pág. {pageNum}</span>
                        </div>
                        {idx < grp.length - 1 && (
                          <button
                            type="button"
                            onClick={() => toggleCut(pageNum)}
                            className="text-[10px] text-muted-foreground hover:text-primary px-2 py-1 border border-dashed rounded self-center"
                            title="Cortar aquí"
                          >
                            <Scissors className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {gi < groups.length - 1 && (
                    <div className="flex justify-center mt-2">
                      <button
                        type="button"
                        onClick={() => toggleCut(grp[grp.length - 1])}
                        className="text-[10px] text-primary hover:underline inline-flex items-center gap-1"
                      >
                        <Undo2 className="h-3 w-3" /> Deshacer este corte
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <span className="text-xs text-muted-foreground mr-auto self-center">
            {groups.length} documento{groups.length === 1 ? "" : "s"} resultante{groups.length === 1 ? "" : "s"}
          </span>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleConfirm}
            disabled={groups.length < 2 || saving || loading}
            className="gap-1"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Scissors className="h-3 w-3" />}
            Separar en {groups.length} archivos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PdfSplitterDialog;
